import { Box, Typography, Chip, Stack, IconButton, Dialog, DialogTitle, DialogContent } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import type { Question, QuizBank } from '../types';

interface PreviewItem {
  bank: QuizBank;
  question: Question;
}

const TYPE_LABELS: Record<string, string> = { single: '单选', multiple: '多选', judge: '判断' };

function normalizeAnswerIsCorrect(answer: string[], optLabel: string, optContent: string): boolean {
  return answer.includes(optLabel) || answer.includes(optContent);
}

interface Props {
  item: PreviewItem | null;
  onClose: () => void;
}

export default function QuestionPreviewDialog({ item, onClose }: Props) {
  return (
    <Dialog
      open={!!item}
      onClose={onClose}
      maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: { borderRadius: 4 } } }}
    >
      {item && (
        <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
            <Chip label={TYPE_LABELS[item.question.type]} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
            <Chip label={item.bank.name} size="small" sx={{ fontWeight: 500 }} />
            <Box sx={{ flex: 1 }} />
            <IconButton size="small" onClick={onClose}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2, fontWeight: 500 }}>
              {item.question.content}
            </Typography>
            <Stack spacing={1}>
              {item.question.options.map(opt => {
                const isCorrect = normalizeAnswerIsCorrect(item.question.answer, opt.label, opt.content);
                return (
                  <Box
                    key={opt.label}
                    sx={{
                      p: 1.5, borderRadius: 2.5, display: 'flex', alignItems: 'center', gap: 1.5,
                      border: '1.5px solid',
                      borderColor: isCorrect ? 'success.main' : 'divider',
                      bgcolor: isCorrect ? 'rgba(76,175,80,0.08)' : 'transparent',
                    }}
                  >
                    <Box
                      sx={{
                        width: 30, height: 30, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        fontWeight: 600, fontSize: '0.8rem',
                        bgcolor: isCorrect ? 'success.main' : 'action.hover',
                        color: isCorrect ? '#fff' : 'text.secondary',
                      }}
                    >
                      {opt.label}
                    </Box>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {opt.content}
                    </Typography>
                    {isCorrect && (
                      <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                        正确答案
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
            {item.question.explanation && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  解析
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {item.question.explanation}
                </Typography>
              </Box>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}

export type { PreviewItem };
